import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDraggable } from '@dnd-kit/core';
import { Folder, Plus } from 'lucide-react';
import { Button, Checkbox, Tooltip, TooltipContent, TooltipTrigger } from '@scio3/ui';
import { cn } from '@/lib/utils';
import { getSelectionRounding } from '@/lib/selection-utils';
import type { Asset } from '@scio3/shared-types';
import { getAssetDef } from '@/features/assets/utils/app-meta';
import { AssetThumbnail } from '@/features/assets/components/AssetThumbnail';
import { useSignedUrl } from '@/lib/file-cdn';
import { usePlaylistDnd, type DragItemData } from '../PlaylistDndProvider';

interface AssetPickerItemProps {
  asset: Asset;
  isSelected: boolean;
  selectedAssetIds: Set<string>;
  isSelfPlaylist?: boolean;
  onToggleSelect: () => void;
  onAddSingle: () => void;
  onOpenAsset: () => void;
  prevSelected?: boolean;
  nextSelected?: boolean;
  /** Whether the user can edit the playlist (folder permission check). */
  canEditPlaylist?: boolean;
}

export function AssetPickerItem({
  asset,
  isSelected,
  selectedAssetIds,
  isSelfPlaylist = false,
  onToggleSelect,
  onAddSingle,
  onOpenAsset,
  prevSelected,
  nextSelected,
  canEditPlaylist = true,
}: AssetPickerItemProps) {
  const { t } = useTranslation();
  const { canEditActivePlaylist } = usePlaylistDnd();
  const displayName = asset.originalFileName || asset._id;

  // Resolve app label from backend appMetas (e.g. "YouTube" instead of "Web")
  const isAppType = asset.type === 'app' || asset.type === 'web';
  const appLabel = isAppType ? (getAssetDef(asset).text ?? null) : null;

  // If this item is part of a multi-selection, drag all selected; otherwise just this one
  const assetIds = useMemo(
    () => (isSelected && selectedAssetIds.size > 1 ? Array.from(selectedAssetIds) : [asset._id]),
    [asset._id, isSelected, selectedAssetIds],
  );

  const dragData = useMemo<DragItemData>(
    () => ({
      source: 'asset-picker',
      assetIds,
      name: displayName,
      thumbnail: asset.thumbnail,
      count: assetIds.length,
    }),
    [assetIds, displayName, asset.thumbnail],
  );

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `asset-${asset._id}`,
    data: dragData,
    // Drag permission depends solely on whether user can edit the center editor's playlist
    disabled: !canEditActivePlaylist,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canEditActivePlaylist ? attributes : {})}
      {...(canEditActivePlaylist ? listeners : {})}
      className={cn(
        'group relative flex items-center gap-2 px-3 py-2 transition-colors',
        canEditActivePlaylist ? 'cursor-grab' : 'cursor-default',
        getSelectionRounding(isSelected, !!prevSelected, !!nextSelected),
        isSelected ? 'bg-primary/10' : 'hover:bg-accent',
        isDragging && 'opacity-50',
      )}
      onClick={onOpenAsset}
    >
      {/* Checkbox (left side) — visible on hover or when selected */}
      <div
        className={cn(
          'flex shrink-0',
          isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelect}
          aria-label={t('Select {{name}}', { name: displayName })}
        />
      </div>

      {/* Thumbnail — uses shared AssetThumbnail (signed S3 URLs, CDN app icons, status overlays) */}
      <div className="h-8 w-10 shrink-0">
        <AssetThumbnail asset={asset} size="sm" className="h-full w-full rounded-md" />
      </div>

      {/* Name + type */}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm">{displayName}</p>
        <p className="text-xs text-muted-foreground capitalize">
          {appLabel || asset.fileType || asset.type || t('file')}
        </p>
      </div>

      {/* Quick-add button — visible on hover, disabled for self-playlist, hidden when not editable */}
      {canEditPlaylist &&
        (isSelfPlaylist ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="hidden h-6 w-6 shrink-0 cursor-not-allowed text-muted-foreground group-hover:flex data-[state=open]:flex"
                aria-label={t('Cannot add a playlist to itself')}
                disabled
                onClick={(e) => e.stopPropagation()}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{t('Cannot add a playlist to itself')}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            size="icon-sm"
            className={cn(
              'hidden h-6 w-6 shrink-0 transition-colors group-hover:flex data-[state=open]:flex',
              isSelected ? 'hover:bg-primary/20' : 'hover:bg-input',
            )}
            aria-label={t('Add to playlist')}
            onClick={(e) => {
              e.stopPropagation();
              onAddSingle();
            }}
          >
            <Plus className="h-4 w-4" />
          </Button>
        ))}
    </div>
  );
}

// Folder row — draggable to editor (adds all assets inside) + clickable to navigate
interface AssetFolderItemProps {
  folder: {
    _id: string;
    name?: string;
    path?: string;
    thumbnail?: string;
  };
  onNavigate: () => void;
  onAddFolder: () => void;
  /** Whether the user can edit the playlist (folder permission check). */
  canEditPlaylist?: boolean;
}

export function AssetFolderItem({
  folder,
  onNavigate,
  onAddFolder,
  canEditPlaylist = true,
}: AssetFolderItemProps) {
  const { t } = useTranslation();
  const { canEditActivePlaylist } = usePlaylistDnd();
  const folderName = folder.name || folder._id;
  const thumbnailUrl = useSignedUrl(folder.thumbnail);
  const [imgError, setImgError] = useState(false);

  const folderPath = folder.path ? `${folder.path}/${folderName}` : `/${folderName}`;

  const dragData = useMemo<DragItemData>(
    () => ({
      source: 'asset-folder',
      folderId: folder._id,
      folderPath,
      name: folderName,
      thumbnail: folder.thumbnail,
    }),
    [folder._id, folderPath, folderName, folder.thumbnail],
  );

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `folder-${folder._id}`,
    data: dragData,
    // Drag permission depends solely on whether user can edit the center editor's playlist
    disabled: !canEditActivePlaylist,
  });

  return (
    <div
      ref={setNodeRef}
      {...(canEditActivePlaylist ? attributes : {})}
      {...(canEditActivePlaylist ? listeners : {})}
      className={cn(
        'group relative flex h-12 items-center gap-2 rounded-lg px-3 hover:bg-accent transition-colors min-w-0',
        canEditActivePlaylist ? 'cursor-grab' : 'cursor-default',
        isDragging && 'opacity-50',
      )}
      onClick={onNavigate}
    >
      {/* Spacer to align with asset item checkboxes */}
      <div className="w-4 shrink-0" />
      <div className="relative flex h-8 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {thumbnailUrl && !imgError ? (
          <>
            <img
              src={thumbnailUrl}
              alt=""
              className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl"
              aria-hidden="true"
              loading="lazy"
            />
            <img
              src={thumbnailUrl}
              alt={folderName}
              className="relative h-full w-full object-contain"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          </>
        ) : (
          <Folder className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{folderName}</span>

      {canEditPlaylist && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="hidden h-6 w-6 shrink-0 transition-colors group-hover:flex data-[state=open]:flex hover:bg-border"
          aria-label={t('Add folder to playlist')}
          onClick={(e) => {
            e.stopPropagation();
            onAddFolder();
          }}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
